import type { WalletProvider } from '../walletProvider/walletProvider.types';

/**
 * A single entry in the wallet provider registry — the registered
 * `WalletProvider` together with the priority it was registered at. Returned
 * by `WalletProviderRegistry.listProviderEntries` for callers that need to
 * reason about *how* a provider was registered (e.g. classifying the source
 * of an injected provider).
 */
export type WalletProviderRegistryEntry = {
  priority: number;
  walletProvider: WalletProvider;
};

// eslint-disable-next-line custom-rules/no-enums
export enum WalletProviderPriority {
  /**
   * Highest priority should be used by wallet providers that implement
   * the most reliable wallet integration.
   * example: The SDK provided by the wallet provider.
   */
  WALLET_SDK = 100,
  /**
   * Medium priority should be used by wallet providers that implement
   * a wallet integration via some reliable standard.
   * example: A wallet provider that uses EIP6963 announcement.
   */
  WALLET_SELF_ANNOUNCEMENT_STANDARD = 50,
  /**
   * Low priority should be used by wallet providers that implement
   * a wallet integration on a less reliable standard.
   * example: A wallet provider that uses window.ethereum, where the
   * window key can be overridden by other extensions.
   */
  WINDOW_INJECT = 20,
}

export type WalletProviderRegistry = {
  /**
   * Retrieves a wallet provider by its key.
   * @param key - The key of the wallet provider.
   * @returns The wallet provider.
   */
  getByKey: (key: string) => WalletProvider | undefined;
  /**
   * Retrieves all registry entries — each entry pairs a wallet provider with
   * the priority it was registered at. Useful for callers that need to know
   * *how* a provider was registered (e.g. classifying the source of an
   * injected provider for the wallet options catalogue).
   * @returns The registry entries.
   */
  listProviderEntries: () => WalletProviderRegistryEntry[];
  /**
   * Retrieves all wallet providers.
   * @returns The wallet providers.
   */
  listProviders: () => WalletProvider[];
  /**
   * Registers a wallet provider.
   * @param args - The arguments for the wallet provider.
   */
  register: (args: {
    priority: WalletProviderPriority;
    walletProvider: WalletProvider;
  }) => void;
  /**
   * Unregisters a wallet provider.
   * @param key - The key of the wallet provider.
   */
  unregister: (key: string) => void;
};
